<template>
  <vs-card class="card">
    <vs-wrapper>
      <v-skeleton-loader v-if="isInitialLoading" type="card-heading" />
      <div v-else class="card-inner">
        <div class="sync-target-icon">
          <SyncTargetImages :logo="logoName" />
        </div>
        <div class="flex-grow-1">
          <div class="top-row">
            <vs-text type="intro" bold>{{ target.name }}</vs-text>
            <v-tooltip bottom v-if="showRefreshButton" open-delay="500">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <vs-button
                    icon="mdi-refresh"
                    type="tertiary"
                    :small="true"
                    :disabled="isFetching"
                    @click="handleManualRefresh"
                  />
                </div>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </div>
          <vs-text>{{ statusText }}</vs-text>
        </div>
      </div>
      <v-row dense v-if="priorityError">
        <batch-sync-history-error-item
          :error="priorityError"
          :see-more-button="true"
          :dense="true"
          :useColor="true"
          @expand="handleShowDetails"
        />
      </v-row>
      <v-row dense v-if="changesSinceLastSync">
        <div class="changes-detected">
          <v-icon>mdi-information-slab-circle-outline</v-icon>
          <vs-text type="caption"
            >This batch changed after the last sync. Your QuickBooks invoice may be out of
            date.</vs-text
          >
        </div>
      </v-row>
      <v-row dense style="align-items: center">
        <v-col v-if="showSyncButton" :class="showOverflowMenu ? 'py-0' : ''">
          <v-tooltip bottom :disabled="batchHasLineItems">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <vs-button
                  full-width
                  :label="syncLabel"
                  type="primary"
                  :loading="isRequestingSync || isSyncQueued"
                  :disabled="!batchHasLineItems"
                  @click="handleSyncClicked"
                />
              </div>
            </template>
            <span
              >The jobs in this batch do not have any line items. Add line items to enable
              syncing.</span
            >
          </v-tooltip>
        </v-col>
        <v-col v-else-if="showInvoiceLink" :class="showOverflowMenu ? 'py-0' : ''">
          <vs-button
            icon="mdi-external-link"
            full-width
            :label="openInvoiceLabel"
            type="secondary"
            :disabled="isRequestingSync"
            @click="handleOpenInvoiceLink"
          ></vs-button>
        </v-col>
        <v-col v-else>
          <v-spacer />
        </v-col>
        <v-col v-if="showOverflowMenu" class="shrink">
          <vs-menu :items="menuItems" />
        </v-col>
      </v-row>
    </vs-wrapper>
  </vs-card>
</template>

<script setup lang="ts">
import SyncTargetImages from './SyncTargetImages.vue'
import { computed, toRef, watch } from 'vue'
import { formatTimeAgo } from '@/utils/date-formatting'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsCard from '@/components/vision/VsCard.vue'
import Batch, { ExternalInvoice, SyncTarget } from '@/models/batch'
import {
  useInvoiceSyncTargetHistoryQuery,
  useInvoiceSyncTargetsQuery,
  useRequestBatchSync,
} from '@/api/invoice'
import VsMenu from '@/components/vision/VsMenu.vue'
import BatchSyncHistoryErrorItem from '@/components/batches/BatchSyncHistoryErrorItem.vue'
import _ from 'lodash'
import moment from 'moment'

const props = defineProps<{
  invoiceId: string
  target: SyncTarget
  batch: Batch
}>()

const emits = defineEmits<{
  (e: 'show-history', targetId: string)
}>()

const invoiceIdRef = toRef(props, 'invoiceId')
const targetRef = toRef(props, 'target')
const targetIdRef = computed(() => targetRef.value.id)
const batchRef = toRef(props, 'batch')
const batchHasLineItems = computed(() => batchRef.value?.hasLineItems ?? false)
const isSyncQueued = computed(() => targetRef.value.status === 'Queued')
const syncRefetchInterval = computed(() => (isSyncQueued.value ? 3_000 : 60_000))

const {
  refetch: refetchSyncTargets,
  isInitialLoading,
  isLoading: isFetching,
} = useInvoiceSyncTargetsQuery(invoiceIdRef, { refetchInterval: syncRefetchInterval })
const { mutate: requestSync, isLoading: isRequestingSync } = useRequestBatchSync()
const { data: history, refetch: refetchHistory } = useInvoiceSyncTargetHistoryQuery(
  invoiceIdRef,
  targetIdRef,
)

watch(
  () => targetRef.value.status,
  async (status, previousStatus) => {
    if (previousStatus === 'Queued' && (status === 'Success' || status === 'Failed')) {
      await refetchHistory()
    }
  },
)

type MenuItem = {
  text: string
  icon: string
  disabled?: boolean | undefined
  onClick: () => void
}

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = []

  if (showDetailsPage.value) {
    items.push({
      text: 'View Details',
      icon: 'mdi-information-slab-circle-outline',
      onClick: handleShowDetails,
    })
  }

  if (showReSyncButton.value) {
    items.push({
      text: 'Re-Sync',
      icon: 'mdi-sync',
      onClick: handleSyncClicked,
      disabled: isRequestingSync.value || isSyncQueued.value || !batchHasLineItems.value,
    })
  }

  return items
})

const logoMap = {
  qbo: 'quickbooks',
  qbd: 'quickbooks',
}

const logoName = computed(() => logoMap[targetRef.value.type.toLowerCase()] || '')

const statusText = computed(() => {
  switch (targetRef.value.status) {
    case '':
      return 'No invoice has been created in QuickBooks yet.'
    case 'Queued':
      return `Queued ${formatTimeAgo(targetRef.value.lastRequestedSyncAt)}`
    case 'Success':
      return targetRef.value.errors.length === 0
        ? `Last synced ${formatTimeAgo(targetRef.value.lastCompletedSyncAt)}`
        : `Synced with warnings ${formatTimeAgo(targetRef.value.lastCompletedSyncAt)}`
    case 'Failed':
      return `Failed ${formatTimeAgo(targetRef.value.lastCompletedSyncAt)}`
    default:
      return ''
  }
})

const priorityError = computed(() => {
  const orderedErrors = _.orderBy(targetRef.value.errors, (x) => x.type === 'Error', 'desc')
  return orderedErrors.length > 0 ? orderedErrors[0] : null
})

const invoiceLink = computed<ExternalInvoice | null>(() => {
  const entriesWithExternalLinks =
    _.orderBy(history.value, (x) => moment(x.startedAt), 'desc').filter(
      (x) => x.externalInvoice !== null,
    ) || []

  return entriesWithExternalLinks[0]?.externalInvoice ?? null
})

const openInvoiceLabel = computed(() => {
  if (invoiceLink.value) {
    const displayName = invoiceLink.value.displayName
    return displayName ? `Open Invoice #${displayName}` : 'Open Invoice'
  }
  return ''
})

const changesSinceLastSync = computed(() => {
  return (
    batchRef.value?.lastModified &&
    moment(targetRef.value.lastRequestedSyncAt).isBefore(batchRef.value.lastModified)
  )
})

const showSyncButton = computed(() => {
  return (
    isRequestingSync.value ||
    targetRef.value.status === '' || // never synced
    targetRef.value.status === 'Queued' || // sync failed
    targetRef.value.status === 'Failed' || // sync failed
    changesSinceLastSync.value === true
  ) // there are changes since last sync
})

const syncLabel = computed(() => {
  if (isRequestingSync.value || isSyncQueued.value) {
    return 'Syncing…'
  }
  if (targetRef.value.status === 'Failed') {
    return 'Retry Sync'
  }
  return 'Sync'
})

const showRefreshButton = computed(() => {
  return targetRef.value.status !== '' // has synced at least once
})

const showInvoiceLink = computed(() => {
  return invoiceLink.value !== null
})

const showOverflowMenu = computed(() => {
  return menuItems.value.length > 1
})

const showDetailsPage = computed(() => {
  return targetRef.value.status !== ''
})

const showReSyncButton = computed(() => {
  return targetRef.value.status !== ''
})

const handleManualRefresh = async (e) => {
  await Promise.all([refetchSyncTargets(), refetchHistory()])
  e.target.blur() // remove focus after click
}

const handleSyncClicked = () => {
  requestSync({
    invoiceId: invoiceIdRef.value,
    targetId: targetRef.value.id,
  })
}

const handleShowDetails = () => {
  emits('show-history', targetIdRef.value)
}

const handleOpenInvoiceLink = () => {
  if (invoiceLink.value != null) {
    window.open(invoiceLink.value.link, '_blank')
  }
}
</script>

<style scoped>
.card {
  border: none;
  box-shadow: var(--shadow-low);
}
.card-inner {
  display: flex;
  flex-direction: row;
  gap: var(--space-base);
}

.sync-target-icon {
  width: 30px;
  flex-shrink: 0;
}

.top-row {
  display: flex;
  flex-direction: row;
  gap: var(--space-base);
  justify-content: space-between;
  align-items: center;
}

.changes-detected {
  padding: var(--space-small) 0;
  margin: var(--space-small);
  display: flex;
  gap: var(--space-smaller);
}
</style>
